import { SetStateAction } from 'react';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import {
  applyDocviewFileNamePattern,
  FetchOptionsFunction,
  fileReducer,
  FileSelection,
  FileSelectionAction,
  FileSelectionState,
  isJsonSchemaDateProperty,
  Options,
  UploadState,
} from '@asset-metrix/portal-ui';
import { JsonError, JsonSchema } from '@sagold/react-json-editor';
import { TFunction } from 'i18next';
import { create } from 'zustand';

import { uploadDocument } from '../../../api/portalGateway/fileUpload';

type PatternValueSourceCache = {
  labelsByValue: Record<string, string>;
};

function toPatternValueSourceCache(options: Options): PatternValueSourceCache {
  const { values, labels } = options;

  return {
    labelsByValue: Object.fromEntries(
      values.map((value, index) => [value || '', labels[index] || ''])
    ),
  };
}

function buildPatternValueCacheByKey(
  schemaProperties: JsonSchema['properties'],
  tableModalValues?: { [p: string]: Options }
): Record<string, PatternValueSourceCache> {
  if (!schemaProperties || !tableModalValues) {
    return {};
  }

  const cache: Record<string, PatternValueSourceCache> = {};

  for (const [key, property] of Object.entries(schemaProperties)) {
    if (
      typeof property !== 'object' ||
      !property.conditionalFilter?.dataProperty ||
      !tableModalValues[property.conditionalFilter?.dataProperty]
    ) {
      continue;
    }

    cache[key] = toPatternValueSourceCache(
      tableModalValues[property.conditionalFilter.dataProperty]
    );
  }

  return cache;
}

function getPatternStringValues(rawValue: unknown): string[] {
  return Array.isArray(rawValue)
    ? rawValue
        .filter(
          (value) =>
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        )
        .map(String)
    : typeof rawValue === 'string' ||
        typeof rawValue === 'number' ||
        typeof rawValue === 'boolean'
      ? [String(rawValue)]
      : [];
}

function resolvePatternValue({
  key,
  source,
  rawValue,
  schemaProperties,
  tableModalValues,
  patternValueCacheByKey,
}: {
  key: string;
  source: 'label' | 'value';
  rawValue: unknown;
  schemaProperties: JsonSchema['properties'];
  tableModalValues?: { [p: string]: Options };
  patternValueCacheByKey: Record<string, PatternValueSourceCache>;
}): string {
  const stringValues = getPatternStringValues(rawValue);
  if (source === 'value') {
    return stringValues.join(', ');
  }

  const schemaProperty = schemaProperties?.[key];
  const dataProperty =
    typeof schemaProperty === 'object'
      ? schemaProperty.conditionalFilter?.dataProperty
      : undefined;
  const keyTableModalValues =
    typeof dataProperty === 'string'
      ? tableModalValues?.[dataProperty]
      : undefined;
  const fallbackSourceCache = keyTableModalValues
    ? toPatternValueSourceCache(keyTableModalValues)
    : undefined;
  const sourceCache = patternValueCacheByKey[key] ?? fallbackSourceCache;
  if (!sourceCache) {
    return stringValues.join(', ');
  }

  return stringValues
    .map((value) => sourceCache.labelsByValue[value] || value)
    .filter(Boolean)
    .join(', ');
}

function createPatternBasedFilename({
  data,
  pattern,
  patternValueCacheByKey,
  schemaProperties,
  tableModalValues,
}: {
  data: Record<string, unknown>;
  pattern: string;
  patternValueCacheByKey: Record<string, PatternValueSourceCache>;
  schemaProperties: JsonSchema['properties'];
  tableModalValues?: { [p: string]: Options };
}): string {
  return applyDocviewFileNamePattern(pattern, {
    onUnresolved: 'omit',
    sanitize: true,
    trim: true,
    resolveValue: ({ key, source }) => ({
      value: resolvePatternValue({
        key,
        source,
        rawValue: data[key],
        schemaProperties,
        tableModalValues,
        patternValueCacheByKey,
      }),
      isDateMetadata: isJsonSchemaDateProperty(schemaProperties?.[key]),
    }),
  });
}

function getMetadataInfoFromJsonSchema(jsonSchema: JsonSchema): Array<{
  key: string;
  label?: string;
  perFileInputInUpload: boolean;
}> {
  if (!jsonSchema.properties) {
    return [];
  }

  return Object.entries(jsonSchema.properties).map(([key, property]) => ({
    key,
    label:
      typeof property === 'object' && typeof property.title === 'string'
        ? property.title
        : undefined,
    perFileInputInUpload:
      typeof property === 'object' &&
      typeof property.options?.perFileInputInUpload === 'boolean'
        ? property.options.perFileInputInUpload
        : false,
  }));
}

type DocUploadConfig = {
  acceptedMimeTypes?: string[];
  contentId: string;
  tableFilterInstanceId: string;
  fileNameMetadataExtractionConfig?: FileUploadMetaDataExtractionDto;
  enableAIMetadataExtraction: boolean;
  enablePatternBasedRenaming: boolean;
  fetchOptions?: FetchOptionsFunction;
  jsonSchema: JsonSchema;
  onClose: () => void;
  onUploadSuccess?: () => Promise<unknown>;
  translation: TFunction;
  uploadFilenamePattern?: string;
  withinPortal?: boolean;
};

export type AIMetadataExtractionStatus =
  'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';

type DocUploadStore = DocUploadConfig & {
  aiExtractionEnabled: boolean;
  aiExtractionExplanations: Record<string, Record<string, string | undefined>>;
  aiMetadataExtractionStatus: Record<string, AIMetadataExtractionStatus>;
  tableModalValues?: { [p: string]: Options };
  editorErrors: JsonError[];
  fileNameMetadataExtractionEnabled: boolean;
  hasPerFileInputInUpload: boolean;
  fileState: FileSelectionState;
  metadataInputsLocked: boolean;
  isTableModalValuesLoading: boolean;
  modalError?: string;
  renameFileWithPattern: Record<string, boolean>;
  storeInitialized: boolean;
  perFileMetaData: Record<string, Record<string, unknown>>;
  metadataInfo: Array<{
    key: string;
    label?: string;
    perFileInputInUpload: boolean;
  }>;
  patternValueCacheByKey: Record<string, PatternValueSourceCache>;
  cleanupAndClose: () => void;
  reset: () => void;
  dispatchFileStateAction: (action: FileSelectionAction) => void;
  uploadAllFiles: (commonMetaData: Record<string, unknown>) => Promise<void>;
  initialize: (config: DocUploadConfig) => void;
  initializeRenameFileWithPattern: (enabled: boolean) => void;
  setAiExtractionExplanations: (
    fileId: string,
    data: Record<string, string | undefined>
  ) => void;
  setTableModalValues: (value: { [p: string]: Options } | undefined) => void;
  setEditorErrors: (errors: JsonError[]) => void;
  setIsTableModalValuesLoading: (value: boolean) => void;
  setMetadataExtractionFlags: (flags: {
    aiExtractionEnabled: boolean;
    fileNameMetadataExtractionEnabled: boolean;
  }) => void;
  setModalError: (error?: string) => void;
  setperFileMetaData: (
    updater: SetStateAction<Record<string, Record<string, unknown>>>
  ) => void;
  setAIMetadataExtractionStatus: (
    fileId: string,
    status: AIMetadataExtractionStatus
  ) => void;
  setRenameFileWithPattern: (fileId: string, enabled: boolean) => void;
  setPatternBasedFilename: (
    fileId: string,
    data: Record<string, unknown>
  ) => void;
  uploadSingleFile: (
    file: FileSelection,
    commonMetaData: Record<string, unknown>
  ) => Promise<boolean>;
};

export const initialTransientState = {
  aiExtractionEnabled: false,
  aiExtractionExplanations: {},
  aiMetadataExtractionStatus: {},
  tableModalValues: undefined,
  editorErrors: [],
  enablePatternBasedRenaming: false,
  fetchOptions: undefined,
  fileNameMetadataExtractionEnabled: false,
  hasPerFileInputInUpload: false,
  fileState: [],
  metadataInputsLocked: false,
  isTableModalValuesLoading: false,
  modalError: undefined,
  storeInitialized: false,
  perFileMetaData: {},
  metadataInfo: [],
  patternValueCacheByKey: {},
  renameFileWithPattern: {},
};

export const useDocUploadStore = create<DocUploadStore>((set, get) => ({
  acceptedMimeTypes: undefined,
  contentId: '',
  tableFilterInstanceId: '',
  fileNameMetadataExtractionConfig: undefined,
  enableAIMetadataExtraction: false,
  jsonSchema: {},
  onClose: () => undefined,
  onUploadSuccess: undefined,
  translation: ((key: string) => key) as TFunction,
  withinPortal: undefined,
  ...initialTransientState,
  initialize: (config) => {
    const { storeInitialized } = get();
    if (storeInitialized) {
      return;
    }
    const {
      jsonSchema,
      fileNameMetadataExtractionConfig,
      enableAIMetadataExtraction,
    } = config;

    const metadataInfo = getMetadataInfoFromJsonSchema(jsonSchema);

    const hasPerFileInputInUpload = metadataInfo.some(
      (info) => info.perFileInputInUpload
    );

    const extractionKey =
      fileNameMetadataExtractionConfig &&
      fileNameMetadataExtractionConfig.enabled &&
      fileNameMetadataExtractionConfig.key;
    const property = jsonSchema.properties?.[extractionKey || ''];

    const aiExtractionEnabled =
      hasPerFileInputInUpload && enableAIMetadataExtraction;
    const fileNameMetadataExtractionEnabled =
      typeof property === 'object' &&
      typeof property.conditionalFilter?.dataProperty === 'string';

    set({
      ...initialTransientState,
      ...config,
      aiExtractionEnabled,
      fileNameMetadataExtractionEnabled,
      hasPerFileInputInUpload,
      metadataInfo,
      storeInitialized: true,
    });
  },
  initializeRenameFileWithPattern: (enabled: boolean) => {
    set((state) => ({
      patternValueCacheByKey: buildPatternValueCacheByKey(
        state.jsonSchema.properties,
        state.tableModalValues
      ),
      renameFileWithPattern: Object.fromEntries(
        state.fileState.map((file) => [file.id, enabled])
      ),
    }));
  },
  setAiExtractionExplanations: (fileId, data) => {
    set((state) => ({
      aiExtractionExplanations: {
        ...state.aiExtractionExplanations,
        [fileId]: data,
      },
    }));
  },

  setMetadataExtractionFlags: ({
    aiExtractionEnabled,
    fileNameMetadataExtractionEnabled,
  }) =>
    set(() => ({
      aiExtractionEnabled,
      fileNameMetadataExtractionEnabled,
    })),
  setIsTableModalValuesLoading: (value) =>
    set(() => ({ isTableModalValuesLoading: value })),
  setTableModalValues: (value) => set(() => ({ tableModalValues: value })),
  setperFileMetaData: (updater) =>
    set((state) => ({
      perFileMetaData:
        typeof updater === 'function'
          ? updater(state.perFileMetaData)
          : updater,
    })),
  setAIMetadataExtractionStatus: (fileId, status) =>
    set((state) => ({
      aiMetadataExtractionStatus: {
        ...state.aiMetadataExtractionStatus,
        [fileId]: status,
      },
    })),
  setRenameFileWithPattern: (fileId, enabled) =>
    set((state) => {
      if (state.renameFileWithPattern[fileId] !== enabled) {
        return {
          renameFileWithPattern: {
            ...state.renameFileWithPattern,
            [fileId]: enabled,
          },
        };
      }
      return state;
    }),
  setPatternBasedFilename: (fileId, data) =>
    set((state) => {
      if (
        !state.enablePatternBasedRenaming ||
        state.renameFileWithPattern[fileId] === false ||
        !state.uploadFilenamePattern ||
        !state.jsonSchema.properties
      ) {
        return state;
      }

      const patternBasedName = createPatternBasedFilename({
        data,
        pattern: state.uploadFilenamePattern,
        patternValueCacheByKey: state.patternValueCacheByKey,
        schemaProperties: state.jsonSchema.properties,
        tableModalValues: state.tableModalValues,
      });
      if (!patternBasedName) {
        return state;
      }

      return {
        fileState: fileReducer(state.fileState, {
          type: 'updateFileName',
          payload: {
            id: fileId,
            name: patternBasedName,
          },
        }),
      };
    }),
  setEditorErrors: (errors) => set(() => ({ editorErrors: errors })),
  setModalError: (error) => set(() => ({ modalError: error })),
  dispatchFileStateAction: (action) =>
    set((state) => ({
      fileState: fileReducer(state.fileState, action),
    })),
  reset: () => {
    set(() => ({
      ...initialTransientState,
    }));
  },
  cleanupAndClose: () => {
    const { onClose, reset } = get();
    reset();
    onClose?.();
  },
  uploadSingleFile: async (file, commonMetaData) => {
    const {
      contentId,
      dispatchFileStateAction,
      jsonSchema,
      perFileMetaData,
      setModalError,
      translation,
    } = get();

    // Overlay step-2 per-file edits on step-1 values. Filename extraction's
    // step-2 schema only includes the filename key, so a visited file's
    // perFileMetaData entry is a partial object and must not replace every
    // per-file key from step 1.
    const mergedMetadata: Record<string, unknown> = {
      ...commonMetaData,
      ...perFileMetaData[file.id],
      // Temporary: add technical metadata definition to all upload request,
      // this is done until a proper implementation of technical metadata definitions is implemented.
      original_file_name: `${file.originalFileName}${file.originalFileExtention}`,
    };

    const updateFileState = (newState: UploadState, error?: string) => {
      dispatchFileStateAction({
        type: 'updateFileSelectionUploadState',
        payload: { id: file.id, newState, error },
      });
    };

    for (const propertyKey in jsonSchema.properties) {
      const property = jsonSchema.properties[propertyKey];
      if (
        typeof property === 'object' &&
        property.options?.selectAll &&
        Array.isArray(mergedMetadata[propertyKey]) &&
        mergedMetadata[propertyKey].length > 10
      ) {
        const error = translation('app.upload.hideAndSelectAllFieldError', {
          title: property?.title ?? '',
        });
        updateFileState('isError', error);
        setModalError(error);
        console.error(error);
        return false;
      }
    }

    updateFileState('isPending');

    try {
      await uploadDocument(file.file, contentId, mergedMetadata);
      updateFileState('isSuccess');
      return true;
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : JSON.stringify(e);
      updateFileState('isError', errorMessage);
      return false;
    }
  },
  uploadAllFiles: async (commonMetaData) => {
    set({ metadataInputsLocked: true });

    const { fileState, onUploadSuccess } = get();

    const results = await Promise.all(
      fileState.map((fileSelection) =>
        get().uploadSingleFile(fileSelection, commonMetaData)
      )
    );

    // Call onUploadSuccess once if at least one file uploaded successfully
    if (results.some(Boolean)) {
      await onUploadSuccess?.();
    }
  },
}));

export type { DocUploadConfig, DocUploadStore };
